import { AbsoluteFill, Easing, Img, interpolate, spring, staticFile, useCurrentFrame, useVideoConfig } from "remotion";
import { preloadImage } from "@remotion/preload";
import './assets/css/Especs.css'
import { useEffect, useLayoutEffect, useRef, useState } from "react";

interface SeccionInterface {
	text1: string,
	text2: string,
	text3: string,
	linkText: string,
	linkUrl: string
}

export const ChatSection = ({ text1, text2, text3, linkText, linkUrl }: SeccionInterface) => {
	const frame = useCurrentFrame();
	const { durationInFrames, fps } = useVideoConfig();
	const bubble1Ref = useRef<HTMLDivElement | null>(null);
	const [bubbled1Width, setBubble1Width] = useState(0);

	const bubble2Ref = useRef<HTMLDivElement | null>(null);
	const [bubbled2Width, setBubble2Width] = useState(0);

	const bubble3Ref = useRef<HTMLDivElement | null>(null);
	const [bubbled3Width, setBubble3Width] = useState(0);

	useLayoutEffect(() => {
		if (bubble1Ref.current) {
			setBubble1Width(bubble1Ref.current.offsetWidth + 100);
		}
		if (bubble2Ref.current) {
			setBubble2Width(bubble2Ref.current.offsetWidth + 200);
		}
		if (bubble3Ref.current) {
			setBubble3Width(bubble3Ref.current.offsetWidth + 180);
		}
	}, [text1, text2, text3]);

	useEffect(() => {
		preloadImage(staticFile(process.env.REACT_APP_URL_PUBLIC_VIDEO + "/Videos/CLARO_01/01_FONDO.jpg"));
		preloadImage(staticFile(process.env.REACT_APP_URL_PUBLIC_VIDEO + "/Videos/CLARO_01/claro-white.svg"));
	}, []);

	const scaleSection = spring({
		fps,
		frame,
		config: { damping: 400, stiffness: 400 },
		from: 1,
		to: 2,
		delay: (durationInFrames - 8)
	});
	const blurSection = spring({
		fps,
		frame,
		config: { damping: 100, stiffness: 100 },
		from: 0,
		to: 10,
		delay: (durationInFrames - 8)
	});

	// ? PHONE

	const opacityPhone = spring({
		fps,
		frame: frame * 3,
		config: { damping: 100, stiffness: 100 },
		from: 0,
		to: 1,
		delay: 0
	});
	const scalePhone = spring({
		fps,
		frame,
		config: { damping: 100, stiffness: 100 },
		from: .5,
		to: 1,
		delay: 0
	});
	const rotatePhone = spring({
		fps,
		frame,
		config: { damping: 100, stiffness: 100 },
		from: -30,
		to: 0,
		delay: 0
	});

	const translatePhone = spring({
		fps,
		frame,
		config: { damping: 100, stiffness: 100 },
		from: 100,
		to: 0,
		delay: 0
	});

	// ? BUBBLE #1
	const bubble1Scale = spring({
		fps,
		frame,
		config: { damping: 300, stiffness: 100 },
		from: 0,
		to: 1,
		delay: 20
	});
	const bubble1Rotate = spring({
		fps,
		frame,
		config: { damping: 300, stiffness: 100 },
		from: -30,
		to: 0,
		delay: 20
	});
	const bubble1TextOpacity = spring({
		fps,
		frame,
		config: { damping: 300, stiffness: 100 },
		from: 0,
		to: 1,
		delay: 50
	});
	const bubble1Width = spring({
		fps,
		frame,
		config: { damping: 10, stiffness: 10 },
		from: 0,
		to: bubbled1Width,
		delay: 20
	});
	const bubble1Translate = spring({
		fps,
		frame,
		config: { damping: 100, stiffness: 100 },
		from: 0,
		to: -140,
		delay: 68
	});
	const bubble1Translate2 = spring({
		fps,
		frame,
		config: { damping: 100, stiffness: 100 },
		from: -140,
		to: -280,
		delay: 140
	});

	const bubbleTranslate1 = interpolate(
		frame,
		[68, 140, 141],
		[
			bubble1Translate,
			-140,
			bubble1Translate2
		],
		{
			extrapolateRight: 'clamp',
			extrapolateLeft: 'clamp'
		}
	);

	// ? BUBBLE #2
	const bubble2Scale = spring({
		fps,
		frame,
		config: { damping: 100, stiffness: 100 },
		from: 0,
		to: 1,
		delay: 68
	});
	const bubble2Rotate = spring({
		fps,
		frame,
		config: { damping: 100, stiffness: 100 },
		from: -30,
		to: 0,
		delay: 68
	});
	const bubble2TextOpacity = spring({
		fps,
		frame,
		config: { damping: 100, stiffness: 100 },
		from: 0,
		to: 1,
		delay: 100
	});
	const bubble2Width = spring({
		fps,
		frame,
		config: { damping: 10, stiffness: 10 },
		from: 0,
		to: bubbled2Width,
		delay: 68
	});
	const bubble2Translate = spring({
		fps,
		frame,
		config: { damping: 100, stiffness: 100 },
		from: 0,
		to: -140,
		delay: 140
	});

	const bubbleTranslate2 = interpolate(
		frame,
		[140, 350],
		[
			bubble2Translate,
			-140,
		],
		{
			extrapolateRight: 'clamp',
			extrapolateLeft: 'clamp',
		}
	);

	// ? BUBBLE #3
	const bubble3Scale = spring({
		fps,
		frame,
		config: { damping: 100, stiffness: 100 },
		from: 0,
		to: 1,
		delay: 140
	});
	const bubble3Rotate = spring({
		fps,
		frame,
		config: { damping: 100, stiffness: 100 },
		from: -30,
		to: 0,
		delay: 140
	});
	const bubble3TextOpacity = spring({
		fps,
		frame,
		config: { damping: 100, stiffness: 100 },
		from: 0,
		to: 1,
		delay: 180
	});
	const bubble3Width = spring({
		fps,
		frame,
		config: { damping: 10, stiffness: 10 },
		from: 0,
		to: bubbled3Width,
		delay: 140
	});

	// ? INFINITE EMOTES ANIMATION
	const loopDuration = 2000;
	const loopFrame = frame % loopDuration;

	// Create a smooth oscillation between -45 and 45 degrees
	const infiniteSmile = interpolate(
		Math.sin(loopFrame / 10), // Using sine for smooth back-and-forth motion
		[-1, 1], // Input range of sine function
		[-45, 45], // Desired rotation range
		{
			easing: Easing.inOut(Easing.ease), // Smooth easing for better effect
		}
	);
	const infiniteHand = interpolate(
		Math.sin(loopFrame / 10), // Using sine for smooth back-and-forth motion
		[-1, 1], // Input range of sine function
		[-10, 10], // Desired rotation range
		{
			easing: Easing.inOut(Easing.ease), // Smooth easing for better effect
		}
	);
	return (
		<AbsoluteFill style={{
			filter: (blurSection > 0) ? `blur(${blurSection}px)` : '',
			transform: `scale(${scaleSection})`
		}}>
			<AbsoluteFill style={{
				backgroundColor: "#B21F1D"
			}}>
				<Img className="fondo" src={staticFile(process.env.REACT_APP_URL_PUBLIC_VIDEO + "/Videos/CLARO_01/08_FONDO.jpg")} style={{
					position: "absolute"
				}} />
			</AbsoluteFill>
			<AbsoluteFill>
				<div className="phone" style={{
					position: "absolute",
					width: "100%",
					bottom: -5,
					left: "50%",
					textAlign: "center",
					opacity: opacityPhone,
					transformOrigin: "bottom",
					transform: `translateX(-50%) translateY(${translatePhone}%) scale(${scalePhone}) rotate(${rotatePhone}deg)`
				}}>
					<Img className="fondo" src={staticFile(process.env.REACT_APP_URL_PUBLIC_VIDEO + "/Videos/CLARO_01/08_PHONE-2.png")} style={{
						width: "100%",
						maxWidth: 1500,
					}} />
				</div>
				<div className="bubbles" style={{
					position: "absolute",
					bottom: 300,
					left: "30%",
					transform: "translateX(-50%)",
				}}>
					<div className="bubble" style={{
						transform: `translateY(${bubbleTranslate1}px)`
					}}>
						<div className="box" style={{
							width: `${bubble1Width}px`,
							transform: `scale(${bubble1Scale}) rotate(${bubble1Rotate}deg)`
						}}>
							<div className="text" ref={bubble1Ref} style={{
								opacity: bubble1TextOpacity,
							}}>{text1}</div>
						</div>
					</div>
					<div className="bubble" style={{
						transform: `translateY(${bubbleTranslate2}px)`
					}}>
						<div className="box" style={{
							width: `${bubble2Width}px`,
							transform: `scale(${bubble2Scale}) rotate(${bubble2Rotate}deg)`
						}}>
							<div className="text" ref={bubble2Ref} style={{
								opacity: bubble2TextOpacity,
							}}>{text2} <span style={{
								position: "absolute",
								top: "50%",
								right: 35,
								display: "inline-block",
								transform: `translateY(-50%) rotate(${infiniteSmile}deg)`
							}}><Img className="fondo" src={staticFile(process.env.REACT_APP_URL_PUBLIC_VIDEO + "/Videos/CLARO_01/01_SMILE.png")} style={{
								width: "100%",
							}} /></span></div>
						</div>
					</div>
					<div className="bubble">
						<div className="box" style={{
							width: `${bubble3Width}px`,
							transform: `scale(${bubble3Scale}) rotate(${bubble3Rotate}deg)`
						}}>
							<div className="text" ref={bubble3Ref} style={{
								opacity: bubble3TextOpacity,
							}}>{text3} <a href={linkUrl}>{linkText}</a> <span style={{
								position: "absolute",
								top: "50%",
								right: 30,
								display: "inline-block",
								transform: `translateY(-50%) translateX(${infiniteHand}px)`
							}}><Img className="fondo" src={staticFile(process.env.REACT_APP_URL_PUBLIC_VIDEO + "/Videos/CLARO_01/01_HAND.png")} style={{
								width: "100%",
							}} /></span></div>
						</div>
					</div>
				</div>
			</AbsoluteFill>
		</AbsoluteFill>
	);
};